import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Loader2, ArrowLeft, Package, Inbox, CheckCircle2 } from "lucide-react";
import { api, formatError } from "@/lib/api";

const STATUS_STYLES = {
  new: "text-primary border-primary/40",
  contacted: "text-amber-400 border-amber-400/40",
  closed: "text-white/50 border-white/20",
};

function Login({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("m3pl_token", data.token);
      onAuthed(data.user);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50 hover:text-primary transition-colors mb-10">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4">/ Control tower</p>
        <h1 className="font-display text-5xl uppercase tracking-tighter mb-10">Admin login</h1>
        <form onSubmit={submit} data-testid="admin-login-form" className="space-y-7">
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email"
              className="mt-2 w-full bg-transparent border-b border-white/15 py-3 text-lg focus:border-primary focus:outline-none transition-colors" />
          </label>
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password"
              className="mt-2 w-full bg-transparent border-b border-white/15 py-3 text-lg focus:border-primary focus:outline-none transition-colors" />
          </label>
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full hover-fill py-4 font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Enter dashboard"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, closed: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([api.get("/quotes"), api.get("/quotes-stats")]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/quotes/${id}`, { status });
    load();
  };
  const remove = async (id) => {
    await api.delete(`/quotes/${id}`);
    toast.success("Deleted");
    load();
  };

  const cards = [
    { label: "Total requests", value: stats.total, icon: Package },
    { label: "New / unhandled", value: stats.new, icon: Inbox },
    { label: "Closed", value: stats.closed, icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10 sticky top-0 bg-[#050505]/90 backdrop-blur-xl z-10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-3 w-3 bg-primary" />
            <span className="font-display uppercase tracking-tight">Freight IQ · Control Tower</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="font-mono text-xs uppercase tracking-widest text-white/50 hover:text-primary transition-colors">View site</Link>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-primary transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-12">
        <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-12">
          {cards.map((c) => (
            <div key={c.label} className="bg-[#080808] p-8 flex items-center justify-between">
              <div>
                <p className="font-display text-5xl text-primary tracking-tighter">{c.value}</p>
                <p className="font-mono text-[11px] uppercase tracking-widest text-white/50 mt-2">{c.label}</p>
              </div>
              <c.icon size={28} className="text-white/20" />
            </div>
          ))}
        </div>

        <h2 className="font-display text-3xl uppercase tracking-tighter mb-6">Quote requests</h2>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>
        ) : quotes.length === 0 ? (
          <p className="py-24 text-center text-white/40 font-mono text-xs uppercase tracking-widest">No submissions yet</p>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.map((q) => (
              <div key={q.id} data-testid="admin-quote-row" className="border border-white/10 p-6 grid lg:grid-cols-12 gap-6 hover:border-white/20 transition-colors">
                <div className="lg:col-span-3">
                  <p className="font-display text-xl">{q.name}</p>
                  <p className="text-white/50 text-sm">{q.email}</p>
                  {q.company && <p className="text-white/40 text-sm">{q.company}</p>}
                  {q.phone && <p className="text-white/40 text-sm">{q.phone}</p>}
                </div>
                <div className="lg:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-primary">{q.service}</p>
                  {(q.origin || q.destination) && <p className="text-white/60 text-sm mt-1">{q.origin || "?"} → {q.destination || "?"}</p>}
                  <p className="font-mono text-[10px] text-white/30 mt-2">{new Date(q.created_at).toLocaleString()}</p>
                </div>
                <div className="lg:col-span-4">
                  <p className="text-white/70 text-sm">{q.message || <span className="text-white/30">—</span>}</p>
                </div>
                <div className="lg:col-span-2 flex lg:flex-col items-start gap-3">
                  <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} data-testid="admin-quote-status"
                    className={`bg-transparent border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLES[q.status]}`}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button onClick={() => remove(q.id)} data-testid="admin-quote-delete" className="text-white/30 hover:text-destructive transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("m3pl_token"));
  const [checking, setChecking] = useState(!!localStorage.getItem("m3pl_token"));

  useEffect(() => {
    if (!checking) return;
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem("m3pl_token");
      setAuthed(false);
    }).finally(() => setChecking(false));
  }, [checking]);

  const logout = () => {
    localStorage.removeItem("m3pl_token");
    setAuthed(false);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-primary" /></div>;
  return authed ? <Dashboard onLogout={logout} /> : <Login onAuthed={() => setAuthed(true)} />;
}
